import type { NavDestination } from '@/types';
import { expandNavGroupsWithHubTabs, findDestinationById } from './registry';
import { railLabelFor } from './hubs';
import type { NavDomain, NavSearchResult } from './types';

/**
 * Synonyms the sidebar filter should honour but that have no business being in
 * a destination's visible label.
 *
 * These are the words a contractor actually types. "COI" is not in the label
 * "Sub Compliance"; "G702" is not in "Pay Applications"; "gantt" is not in
 * "Project Schedule". Without them, filter-as-you-type across eighty
 * destinations only works for people who already know the product's vocabulary,
 * which defeats the point.
 */
export const NAV_KEYWORDS: Record<string, readonly string[]> = {
  'quickbooks-reports': [
    'profit loss',
    'balance sheet',
    'cash flow',
    'aging',
    'reports',
    '1099',
    'quickbooks',
  ],
  'accounting-workspace': [
    'receivables',
    'payables',
    'invoice',
    'bill',
    'customer',
    'job',
    'accountant',
    'quickbooks',
    'desktop',
  ],
  'finance-taxes': ['tax', 'filing', 'accountant', 'returns', 'irs'],
  'bank-statements': ['statement', 'pdf', 'bank', 'account', 'card'],
  'bank-spending': [
    'monthly costs',
    'recurring',
    'subscriptions',
    'expenses',
    'price changes',
    'payment calendar',
  ],

  'executive-summary': [
    'executive summary',
    'home',
    'dashboard',
    'kpi',
    'alerts',
    'overview',
    'command center',
    'ceo',
    'margin',
    'backlog',
    'revenue',
    'portfolio health',
  ],
  'm365-workspace': [
    'm365',
    'my day',
    'microsoft 365',
    'outlook',
    'onedrive',
    'onenote',
    'calendar',
    'todo',
    'mail',
    'workspace',
    'sharepoint',
    'agent desk',
  ],
  'google-workspace': [
    'google workspace',
    'gmail',
    'google calendar',
    'google drive',
    'directory',
    'g suite',
    'workspace',
  ],
  'ops-dashboard': ['my day', 'operations', 'superintendent', 'blocked'],

  'market-intelligence': ['competitors', 'permits', 'opportunities', 'demand', 'win probability'],
  'cost-database': ['unit cost', 'assemblies', 'price book', 'catalog', 'rates'],
  takeoff: ['measure', 'quantities', 'drawings', 'plans', 'sheets', 'scale'],
  estimates: [
    'estimating',
    'pricing',
    'line items',
    'cost codes',
    'proposal',
    'estimations',
    'bids',
    'company cost',
    'estimate run',
  ],
  comping: ['quotes', 'vendor pricing', 'landed cost', 'freight', 'comparison'],
  'labor-plan': ['crew', 'hours', 'production rate', 'burden', 'manpower', 'labour'],
  'bid-assembly': ['bid', 'margin', 'bond', 'contingency', 'markup', 'tender'],
  'bid-counterparties': ['counterparty', 'general contractor', 'gc', 'bid to', 'win margin'],
  'bid-history': ['hit rate', 'win rate', 'loss', 'win loss', 'results'],
  'estimating-config': [
    'config',
    'criteria',
    'go no-go',
    'overhead',
    'margin',
    'contingency',
    'capacity',
    'settings',
  ],
  'portfolio-showcases': ['case study', 'prequalification', 'prequal', 'marketing', 'references'],
  marketing: ['social', 'instagram', 'facebook', 'posts', 'approval', 'publish', 'website feed'],
  'marketing-brand-studio': [
    'email signature',
    'business card',
    'vcard',
    'qr',
    'logo',
    'background',
    'headshot',
  ],

  'project-portfolio': ['projects', 'jobs', 'table', 'kanban', 'risk rank'],
  'project-overview': ['job overview', 'stakeholders', 'health', 'project controls'],
  'project-map': ['map', 'locations', 'gis', 'markers', 'geography'],
  'project-budget': ['budget', 'cost codes', 'forecast', 'commitments', 'actuals'],
  'project-schedule': ['gantt', 'milestones', 'lookahead', 'critical path', 'baseline'],
  scheduling: ['critical path', 'constraints', 'recovery', 'float', 'milestones'],
  rfis: ['request for information', 'ball in court', 'questions'],
  submittals: ['shop drawings', 'review cycle', 'spec section', 'approvals'],
  'change-orders': ['co', 'variation', 'extras', 'pco', 'claim'],
  'portal-selections': ['allowance', 'client selections', 'finishes', 'options'],
  documents: [
    'drawings',
    'specs',
    'contracts',
    'files',
    'plans',
    'folders',
    'versions',
    'uploads',
    'filing',
    'attachments',
    'library',
  ],
  'digital-twin': ['bim', '3d', 'model', 'ifc', 'zones'],

  'field-today': ['today', 'daily', 'phone', 'mobile', 'my jobs', 'field operations'],
  'field-dispatch': ['crew', 'who is on site', 'assignments', 'foreman'],
  'field-daily-reports': ['daily log', 'dailies', 'weather', 'manpower log'],
  'field-tasks': ['punch', 'todo', 'blocked', 'work items'],
  'field-observations': ['safety observation', 'quality issue', 'near miss', 'findings'],
  'field-rfis': ['request for information', 'raise rfi', 'procore rfi', 'ball in court'],
  'site-telemetry': ['drone', 'sensors', 'cameras', 'scans', 'progress capture'],
  safety: ['trir', 'incidents', 'osha', 'toolbox', 'inspections', 'ppe'],
  quality: ['punch list', 'qa', 'qc', 'warranty', 'deficiency', 'inspections'],
  approvals: ['approve', 'reject', 'sign-off', 'decision', 'ai recommendation', 'purchase order'],
  resources: ['crews', 'equipment', 'workforce', 'utilization', 'fleet', 'labour', 'people'],

  finance: ['revenue', 'margin', 'wip', 'cash flow', 'ar', 'ap', 'profitability', 'financials'],
  'finance-subjects': [
    'customers',
    'vendors',
    'client accounts',
    'cost codes',
    'qbo import',
    'quickbooks import',
    'imported masters',
  ],
  'finance-analytics': ['charts', 'trends', 'ageing', 'drilldown'],
  'billing-applications': [
    'aia',
    'g702',
    'g703',
    'pay app',
    'invoice',
    'schedule of values',
    'sov',
  ],
  'billing-collections': [
    'collections',
    'aging',
    'receivables',
    'owner payment',
    'dunning',
    'follow-up',
    'ar board',
  ],
  'retainage-register': ['retention', 'held back', 'release'],
  'job-cost-export': ['job costing', 'cost code', 'csv', 'variance', 'spend'],
  'payroll-exports': ['timesheet', 'hours', 'wh-347', 'prevailing wage', 'overtime', 'payroll'],
  'payroll-cost': [
    'payroll cost',
    'cost board',
    'gross',
    'burden',
    'hourly rate',
    'per run',
    'qbo payroll',
    'provider cost',
  ],
  // Renamed from "Bank Accounts" to the Accounting workspace — the
  // QuickBooks mapping/sync keywords that used to key their own standalone
  // destinations now fold in here, the same way register/reconciliation
  // already did.
  'bank-accounts': [
    'cash',
    'balances',
    'plaid',
    'operating account',
    'register',
    'ledger',
    'transactions',
    'statement',
    'reconcile',
    'match',
    'clearing',
    'reconciliation',
    'quickbooks',
    'qbo',
    'mapping',
    'chart of accounts',
    'sync',
    'push',
    'run history',
  ],

  'crm-client-accounts': [
    'crm',
    'customers',
    'contacts',
    'owners',
    'accounts',
    'client accounts',
    'relationships',
  ],
  vendors: [
    'vendors',
    'suppliers',
    'subcontractors',
    'catalog',
    'sku',
    'qbo vendors',
    'linked vendors',
    'relationships',
    'catalog ingest',
    'vendor csv',
    'price list',
    'sku upsert',
  ],
  'unified-crm': [
    'relationships',
    'contacts',
    'clients',
    'vendors',
    'subcontractors',
    'internal staff',
    'company profiles',
    'email history',
    'enrichment',
    'import contacts',
    'export contacts',
  ],
  'inbox-triage': ['email', 'mail', 'messages', 'threads', 'triage', 'ai email overview'],
  'estimating-equipment': ['equipment', 'tools', 'machinery', 'rental'],
  'bid-intake': [
    'mailbox',
    'sync',
    'invitation',
    'itb',
    'email ingest',
    'lead funnel',
    'queue',
    'bid intake',
    'working record',
  ],
  'compliance-board': [
    'coi',
    'insurance',
    'certificate',
    'subcontractor',
    'w-9',
    'licence',
    'license',
  ],
  procurement: ['vendors', 'purchase orders', 'po', 'materials', 'suppliers', 'lead time'],
  'time-automation': [
    'timesheet',
    'timecard',
    'time capture',
    'clockshark',
    'shifts',
    'dry run',
    'go live',
    'feature flags',
    'automation controls',
  ],
  'time-automation-provisioning': [
    'job number',
    'job numbering',
    'teams channel',
    'sharepoint folder',
    'activate job',
    'graph',
    'microsoft 365',
  ],
  'time-automation-field-time': [
    'punch',
    'clock in',
    'clock out',
    'missed punch',
    'split time',
    'cost code',
    'prevailing wage',
    'crew defaults',
  ],
  'time-automation-reconciliation': [
    'clockshark cutover',
    'parallel run',
    'reconcile hours',
    'retire clockshark',
  ],
  'time-automation-field-time-setup': [
    'shifts connection',
    'payroll lock',
    'lock period',
    'time thresholds',
    'user map',
  ],
  'time-automation-monitoring': [
    '52-c',
    'electronic monitoring',
    'monitoring notice',
    'acknowledgment',
    'consent',
    'location tracking',
  ],

  simulations: ['what if', 'scenario', 'modelling', 'forecast'],
  reports: [
    'analytics',
    'dashboards',
    'report builder',
    'packets',
    'library',
    'pdf',
    'packs',
    'brief',
    'export',
    'schedule',
  ],
  'ai-agents': ['advisor', 'agents', 'automation', 'recommendations'],

  'client-launch': [
    'onboarding',
    'implementation',
    'meyer',
    'vendor spend',
    'launch center',
    'discovery',
    'uat',
    'master tracker',
    'outstanding work',
    'task owner',
    'launch readiness',
    'platform evaluation',
    'platform gaps',
    'completion requirements',
  ],
};

function haystackFor(destination: NavDestination, domain: NavDomain): string {
  return [
    destination.label,
    // Personal menu names must not make a product page undiscoverable.
    findDestinationById(destination.id)?.label ?? '',
    railLabelFor(destination.id, destination.label),
    ...(NAV_KEYWORDS[destination.id] ?? []),
    destination.description,
    destination.persona ?? '',
    destination.purpose ?? '',
    destination.path,
    domain.label,
    ...domain.keywords,
  ]
    .join(' ')
    .toLowerCase();
}

/** Lower is better. Ranks a label prefix above a label hit above anything else. */
function scoreOf(destination: NavDestination, domain: NavDomain, token: string): number {
  const label = destination.label.toLowerCase();
  if (label.startsWith(token)) return 0;
  if (label.includes(token)) return 1;
  if ((NAV_KEYWORDS[destination.id] ?? []).some((keyword) => keyword.includes(token))) return 2;
  if (domain.label.toLowerCase().includes(token)) return 3;
  if (destination.path.toLowerCase().includes(token)) return 4;
  return 5;
}

/**
 * Filter the permitted tree by a free-text query.
 *
 * Every whitespace-separated token must match somewhere, so "bank rec" narrows
 * where "bank" alone would not. The input is already RBAC-filtered by the
 * caller — search never widens what a user may see.
 */
export function searchNavigation(
  groups: readonly NavDomain[],
  query: string,
  permissions?: readonly string[],
): NavSearchResult[] {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];

  const tree = expandNavGroupsWithHubTabs(groups, permissions);
  const results: NavSearchResult[] = [];
  for (const domain of tree) {
    for (const destination of domain.items) {
      const haystack = haystackFor(destination, domain);
      if (!tokens.every((token) => haystack.includes(token))) continue;
      const score = Math.min(...tokens.map((token) => scoreOf(destination, domain, token)));
      results.push({
        destination,
        domainId: domain.id,
        domainLabel: domain.label,
        score,
      });
    }
  }

  return results.sort(
    (a, b) => a.score - b.score || a.destination.label.localeCompare(b.destination.label),
  );
}
